/**
 * The widget canvas (`specs/M10-D1-dashboards.md` §8.1) — the surface a board's *Dashboard* view
 * and a standalone dashboard both mount.
 *
 * Every number here is measured, not chosen: the grid is 12 columns with a 16 px margin and a
 * 56 px row (so a 6-row card is 416 px, exactly the card on production), the card header is
 * 49 px with its content row at y = 9, and the resize handle is 16 × 16 at the bottom-right.
 * The measurements live in `dashboards.css`; this file is the behaviour.
 */
import { widgetRegistry, WIDGET_GRID, type WidgetLayout } from '@elizmonday/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConfirm } from '@elizmonday/ui';
import { ChartWidget } from './widgets/ChartWidget';
import { BatteryWidget, NumbersWidget, TextWidget, WidgetPlaceholder } from './widgets/SimpleWidgets';
import { widgetSettingsForm } from './widgets/SettingsForms';
import { CanvasFilterPane, CanvasPersonPane, describeCanvasFilter } from './CanvasFilterPane';
import { useDashboardRealtime } from './useDashboardRealtime';
import type { CanvasSettingsOptions, DashboardSource, WidgetRow } from './types';
import './dashboards.css';

/**
 * The grab in progress, shared by the grip and the grid it is dropped on. One ref rather than
 * state: a drag must not re-render the canvas on every pointer move.
 */
interface WidgetDrag {
  id: string;
  layout: WidgetLayout;
  startX: number;
  startY: number;
  /** The browser's own drag controller took over (the grip is `draggable`), so the pointer
   *  handlers must not commit a second time. */
  native: boolean;
}

/** Pointer travel → whole grid units, the same arithmetic `ResizeHandle` resizes in. */
function layoutAfterDrag(
  grid: Element | null,
  drag: WidgetDrag,
  clientX: number,
  clientY: number,
): WidgetLayout {
  const gridWidth = grid?.getBoundingClientRect().width ?? 1073;
  const unitX = (gridWidth - WIDGET_GRID.margin * (WIDGET_GRID.columns - 1)) / WIDGET_GRID.columns;
  const unitY = WIDGET_GRID.rowHeight + WIDGET_GRID.margin;
  const dx = Math.round((clientX - drag.startX) / (unitX + WIDGET_GRID.margin));
  const dy = Math.round((clientY - drag.startY) / unitY);
  return moveBy(drag.layout, dx, dy);
}

/**
 * Where a widget lands. It is clamped to the grid and nothing else moves: what monday does to
 * the **other** cards when one is dropped on them — push down, swap, or float — is not captured
 * on this account (`docs/open-questions.md` q535), so no collision rule is invented here.
 */
function moveBy(layout: WidgetLayout, dx: number, dy: number): WidgetLayout {
  return {
    ...layout,
    x: Math.max(0, Math.min(WIDGET_GRID.columns - layout.w, layout.x + dx)),
    y: Math.max(0, layout.y + dy),
  };
}

const sameLayout = (a: WidgetLayout, b: WidgetLayout): boolean =>
  a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

/** The four kinds the empty canvas offers as one-click presets (§8.1). */
const PRESETS = ['chart', 'numbers', 'battery', 'text'] as const;

export interface WidgetCanvasProps {
  source: DashboardSource;
  /** The board's *New item* button belongs to a board view, not to a standalone dashboard. */
  showNewItem?: boolean;
  onNewItem?: (() => void) | undefined;
  measureTitle?: ((key: string) => string) | undefined;
}

export function WidgetCanvas({ source, showNewItem = false, onNewItem, measureTitle }: WidgetCanvasProps) {
  const { data: widgets, isPending } = source.useWidgets();
  const { data: options } = source.useSettingsOptions();
  const [fullscreen, setFullscreen] = useState<string | null>(null);
  const [settingsFor, setSettingsFor] = useState<string | null>(null);
  const drag = useRef<WidgetDrag | null>(null);

  /*
   * §6.2 and §6.3, in one subscription: `widget.*` re-reads the grid and the three data events
   * go through the debounced `source.refresh`. P-182 (nothing called `refresh`) and P-183 (no
   * second tab ever repainted) were the same missing hook, one layer apart.
   */
  const refresh = source.refresh;
  const reload = source.reload;
  useDashboardRealtime({
    parent: source.parent,
    boardIds: source.boardIds,
    onWidgets: useCallback(() => reload(), [reload]),
    onData: useCallback((stamp: string) => refresh(stamp), [refresh]),
  });

  // §8.2: Escape leaves full screen, which is what every other overlay in this app does.
  useEffect(() => {
    if (fullscreen === null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFullscreen(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const commitDrag = useCallback(
    (grid: Element | null, clientX: number, clientY: number) => {
      const origin = drag.current;
      drag.current = null;
      if (origin === null || source.mutations === null) return;
      const next = layoutAfterDrag(grid, origin, clientX, clientY);
      // A drop where the drag began writes nothing — the same no-op rule the resize obeys
      // (D1 acceptance W2).
      if (sameLayout(next, origin.layout)) return;
      source.mutations.move([{ id: origin.id, ...next }]);
    },
    [source.mutations],
  );

  if (isPending) {
    return <div className="ez-widget-canvas" data-testid="widget-canvas-loading" />;
  }

  const rows = widgets ?? [];
  const shown = fullscreen === null ? null : rows.find((row) => row.id === fullscreen);
  const settingsWidget = settingsFor === null ? null : (rows.find((row) => row.id === settingsFor) ?? null);

  return (
    <div className="ez-widget-canvas" data-testid="widget-canvas">
      <WidgetToolbar source={source} showNewItem={showNewItem} onNewItem={onNewItem} options={options} />
      {rows.length === 0 ? (
        <EmptyCanvas source={source} />
      ) : (
        <div
          className="ez-widget-grid"
          data-testid="widget-grid"
          /* The HTML5 half of the grip's drag: a drop only fires where `dragover` is prevented. */
          onDragOver={(event) => {
            if (drag.current === null) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
          }}
          onDrop={(event) => {
            if (drag.current === null) return;
            event.preventDefault();
            commitDrag(event.currentTarget, event.clientX, event.clientY);
          }}
        >
          {rows.map((row) => (
            <WidgetCard
              key={row.id}
              widget={row}
              source={source}
              measureTitle={measureTitle}
              drag={drag}
              onCommitDrag={commitDrag}
              onFullscreen={() => setFullscreen(row.id)}
              onSettings={() => setSettingsFor(row.id)}
            />
          ))}
        </div>
      )}
      {shown === undefined || shown === null ? null : (
        <div className="ez-widget-fullscreen" data-testid="widget-fullscreen">
          <WidgetCard
            widget={{ ...shown, layout: { ...shown.layout, x: 0, y: 0 } }}
            source={source}
            measureTitle={measureTitle}
            drag={drag}
            onCommitDrag={commitDrag}
            fullscreen
            onFullscreen={() => setFullscreen(null)}
            onSettings={() => setSettingsFor(shown.id)}
          />
        </div>
      )}
      {settingsWidget === null ? null : (
        <WidgetSettingsPane
          widget={settingsWidget}
          source={source}
          options={options}
          onClose={() => setSettingsFor(null)}
        />
      )}
    </div>
  );
}

/**
 * A widget's settings (`specs/M10-D2-widgets.md` §5, **P-181**). The controls are the kind's own
 * `settingsSchema`, rendered by its form in `widgets/SettingsForms.tsx`; this is only the frame,
 * the commit and the `Escape` every overlay in this app answers (P-013).
 */
function WidgetSettingsPane({
  widget,
  source,
  options,
  onClose,
}: {
  widget: WidgetRow;
  source: DashboardSource;
  options: CanvasSettingsOptions | undefined;
  onClose: () => void;
}) {
  const definition = widgetRegistry.find(widget.registryId);
  const Form = widgetSettingsForm(widget.registryId);
  /*
   * The pane edits a **draft**, not the row. Each control commits `{...draft, ...patch}`, and a
   * draft rebuilt from `widget.settings` on every change would lose whichever writes had not
   * round-tripped yet — six controls touched in a second would leave two of them behind.
   */
  const [draft, setDraft] = useState<Record<string, unknown>>(widget.settings);
  const editing = useRef(widget.id);
  if (editing.current !== widget.id) {
    editing.current = widget.id;
    setDraft(widget.settings);
  }

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  return (
    <aside className="ez-widget-settings" data-testid="widget-settings" aria-label={`${widget.name} settings`}>
      <header className="ez-widget-settings__header">
        <strong>{definition?.title ?? widget.kind} settings</strong>
        <button type="button" aria-label="Close widget settings" data-testid="widget-settings-close" onClick={onClose}>
          ✕
        </button>
      </header>
      {Form === null || source.mutations === null ? (
        <p className="ez-widget-settings__hint" data-testid="widget-settings-none">
          {`${definition?.title ?? widget.kind} has nothing to configure yet.`}
        </p>
      ) : (
        <Form
          widget={{ ...widget, settings: draft }}
          options={options}
          data-testid="widget-settings-form"
          onChange={(patch) => {
            const next = { ...draft, ...patch };
            setDraft(next);
            source.mutations?.update(widget.id, next);
          }}
        />
      )}
    </aside>
  );
}

/**
 * The toolbar row (§2.3). `New item` is board-only; `Add widget` is the one control the empty
 * state and the populated canvas share, and it keeps monday's measured 119 × 32.
 */
function WidgetToolbar({
  source,
  showNewItem,
  onNewItem,
  options,
}: {
  source: DashboardSource;
  showNewItem: boolean;
  onNewItem?: (() => void) | undefined;
  options: CanvasSettingsOptions | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [pane, setPane] = useState<'person' | 'filter' | null>(null);
  const filter = source.filter;
  const summary = useMemo(() => describeCanvasFilter(filter?.value ?? null), [filter?.value]);

  return (
    <div className="ez-widget-toolbar" data-testid="widget-toolbar">
      {showNewItem ? (
        <button type="button" className="ez-button ez-button--primary" data-testid="widget-new-item" onClick={onNewItem}>
          New item
        </button>
      ) : null}
      {source.mutations === null ? null : (
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="ez-button ez-button--secondary"
            data-testid="widget-add"
            style={{ width: 119 }}
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
          >
            + Add widget
          </button>
          {open ? <WidgetGallery source={source} onClose={() => setOpen(false)} /> : null}
        </div>
      )}
      {/*
        §2.3's two measured controls — Person 87 × 32 and Filter 95 × 32 — over the `filter` the
        dashboard row already stored and round-tripped and that nothing could write (P-184). What
        monday's own panes *contain* is q537, so both are built from this canvas's own boards and
        columns and neither claims to be a capture.
      */}
      {filter === undefined ? null : (
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="ez-button ez-button--secondary"
            data-testid="widget-person"
            style={{ width: 87 }}
            aria-expanded={pane === 'person'}
            onClick={() => setPane((current) => (current === 'person' ? null : 'person'))}
          >
            Person
          </button>
          {pane === 'person' ? (
            <CanvasPersonPane
              options={options}
              data-testid="widget-person-pane"
              value={filter.value}
              onChange={(next) => filter.set(next)}
              onClose={() => setPane(null)}
            />
          ) : null}
        </div>
      )}
      {filter === undefined ? null : (
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="ez-button ez-button--secondary"
            data-testid="widget-filter"
            style={{ minWidth: 95 }}
            aria-expanded={pane === 'filter'}
            onClick={() => setPane((current) => (current === 'filter' ? null : 'filter'))}
          >
            {summary === null ? 'Filter' : `Filter · ${summary}`}
          </button>
          {pane === 'filter' ? (
            <CanvasFilterPane
              options={options}
              data-testid="widget-filter-pane"
              value={filter.value}
              onChange={(next) => filter.set(next)}
              onClose={() => setPane(null)}
            />
          ) : null}
        </div>
      )}
      <span className="ez-widget-toolbar__spacer" />
    </div>
  );
}

/**
 * The *Add widget* gallery. monday's own catalogue could not be captured on this account
 * (`discovery/ui-reference/dashboards/INDEX.md` §Blocked), so this is the **registry**, rendered:
 * every kind, in gallery order, with the unbuilt ones disabled and naming their module. It
 * cannot drift from what we support, which is the one property monday's own gallery would not
 * have given us.
 */
function WidgetGallery({ source, onClose }: { source: DashboardSource; onClose: () => void }) {
  const definitions = widgetRegistry.list().sort((a, b) => a.galleryOrder - b.galleryOrder);
  return (
    <div className="ez-widget-menu" data-testid="widget-gallery" style={{ position: 'absolute', insetBlockStart: 36 }}>
      {definitions.map((definition) => (
        <button
          key={definition.id}
          type="button"
          className="ez-widget-menu__item"
          data-testid={`widget-gallery-${definition.id}`}
          disabled={!definition.implemented}
          title={definition.implemented ? definition.description : `Lands with ${definition.plannedIn}`}
          onClick={() => {
            source.mutations?.add(definition.id);
            onClose();
          }}
        >
          {definition.title}
          {definition.implemented ? '' : ' · soon'}
        </button>
      ))}
    </div>
  );
}

function EmptyCanvas({ source }: { source: DashboardSource }) {
  return (
    <div className="ez-widget-canvas__empty" data-testid="widget-canvas-empty">
      <div className="ez-widget-empty-card">
        <p className="ez-widget-empty-card__title">Build a dashboard</p>
        <p className="ez-widget-empty-card__hint">
          A dashboard is a grid of widgets over the boards it connects. Add one below, then pick the
          column it counts, sums or charts.
        </p>
      </div>
      {source.mutations === null ? null : (
        <div className="ez-widget-presets">
          <span className="ez-widget-presets__label">Start with</span>
          {PRESETS.map((id) => {
            const definition = widgetRegistry.find(id);
            if (definition === undefined) return null;
            return (
              <button
                key={id}
                type="button"
                className="ez-widget-preset"
                data-testid={`widget-preset-${id}`}
                onClick={() => source.mutations?.add(id)}
              >
                {definition.title}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** A widget card: the measured chrome, plus the body its registry entry chooses. */
function WidgetCard({
  widget,
  source,
  measureTitle,
  drag,
  onCommitDrag,
  fullscreen = false,
  onFullscreen,
  onSettings,
}: {
  widget: WidgetRow;
  source: DashboardSource;
  measureTitle?: ((key: string) => string) | undefined;
  drag: React.MutableRefObject<WidgetDrag | null>;
  onCommitDrag: (grid: Element | null, clientX: number, clientY: number) => void;
  fullscreen?: boolean;
  onFullscreen: () => void;
  onSettings: () => void;
}) {
  const { data } = source.useWidgetData(widget.id);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const confirm = useConfirm();
  const style = fullscreen
    ? undefined
    : {
        gridColumn: `${widget.layout.x + 1} / span ${Math.min(widget.layout.w, WIDGET_GRID.columns)}`,
        gridRow: `${widget.layout.y + 1} / span ${widget.layout.h}`,
      };

  return (
    <section
      className="ez-widget-card"
      data-testid="widget-card"
      data-widget-id={widget.id}
      data-widget-kind={widget.registryId}
      style={style}
    >
      <header className="ez-widget-card__header" data-testid="widget-card-header">
        <WidgetGrip widget={widget} source={source} drag={drag} onCommitDrag={onCommitDrag} />
        <span className="ez-widget-card__title" data-testid="widget-title">
          {source.mutations === null ? widget.name : <WidgetTitleInput widget={widget} source={source} />}
        </span>
        <span className="ez-widget-card__spacer" />
        <div className="ez-widget-card__actions">
          <button
            type="button"
            /* Its own modifier beside the grip's sizing: a phone hides the **drag** grip and the
               resize handle (neither gesture exists on touch) and must keep this one. */
            className="ez-widget-card__grip ez-widget-card__menu"
            data-testid="widget-menu-button"
            aria-label={`${widget.name} options`}
            onClick={(event) => {
              const box = event.currentTarget.getBoundingClientRect();
              setMenu({ x: box.right - 200, y: box.bottom + 4 });
            }}
          >
            ⋯
          </button>
        </div>
      </header>
      <div className="ez-widget-card__body" data-testid="widget-card-body">
        <WidgetBody widget={widget} data={data} measureTitle={measureTitle} />
        {fullscreen ? null : (
          <ResizeHandle widget={widget} source={source} />
        )}
      </div>
      {menu === null ? null : (
        <WidgetMenu
          at={menu}
          fullscreen={fullscreen}
          onClose={() => setMenu(null)}
          onSettings={
            source.mutations === null
              ? null
              : () => {
                  setMenu(null);
                  onSettings();
                }
          }
          onFullscreen={() => {
            setMenu(null);
            onFullscreen();
          }}
          onRemove={
            source.mutations === null
              ? null
              : () => {
                  setMenu(null);
                  /*
                   * P-012: deleting a widget took its configuration — the board it reads, its
                   * filters, its chart settings — with it on the first click, with nothing to
                   * undo it. monday's shape for this is a question, a sentence and Cancel /
                   * <verb> (`discovery/ui-reference/confirmations/item-delete.json`).
                   */
                  void (async () => {
                    const ok = await confirm({
                      title: `Delete ${widget.name || 'this widget'}?`,
                      body: 'The widget and its settings are removed from the dashboard. This cannot be undone.',
                      confirmLabel: 'Delete widget',
                      danger: true,
                    });
                    if (!ok) return;
                    source.mutations?.remove(widget.id);
                  })();
                }
          }
        />
      )}
    </section>
  );
}

function WidgetBody({
  widget,
  data,
  measureTitle,
}: {
  widget: WidgetRow;
  data: ReturnType<DashboardSource['useWidgetData']>['data'];
  measureTitle?: ((key: string) => string) | undefined;
}) {
  const definition = widgetRegistry.find(widget.registryId);
  if (definition === undefined || !definition.implemented) {
    return <WidgetPlaceholder title={definition?.title ?? widget.kind} plannedIn={definition?.plannedIn ?? null} />;
  }
  // Settings always come back through the kind's own schema, so a widget written by an older
  // build (or by the importer) still renders against the shape this build expects.
  const settings = definition.settingsSchema.parse(widget.settings);
  switch (definition.id) {
    case 'chart':
      return <ChartWidget settings={settings} data={data} measureTitle={measureTitle} />;
    case 'numbers':
      return <NumbersWidget settings={settings} data={data} />;
    case 'battery':
      return <BatteryWidget settings={settings} data={data} />;
    case 'text':
      return <TextWidget settings={settings} />;
    default:
      return <WidgetPlaceholder title={definition.title} plannedIn={definition.plannedIn} />;
  }
}

function WidgetMenu({
  at,
  fullscreen,
  onClose,
  onSettings,
  onFullscreen,
  onRemove,
}: {
  at: { x: number; y: number };
  fullscreen: boolean;
  onClose: () => void;
  onSettings: (() => void) | null;
  onFullscreen: () => void;
  onRemove: (() => void) | null;
}) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  return (
    <div className="ez-widget-menu" data-testid="widget-menu" style={{ insetInlineStart: at.x, insetBlockStart: at.y }}>
      {onSettings === null ? null : (
        <button type="button" className="ez-widget-menu__item" data-testid="widget-menu-settings" onClick={onSettings}>
          Settings
        </button>
      )}
      <button type="button" className="ez-widget-menu__item" data-testid="widget-menu-fullscreen" onClick={onFullscreen}>
        {fullscreen ? 'Exit full screen' : 'Open in full screen'}
      </button>
      {onRemove === null ? null : (
        <button type="button" className="ez-widget-menu__item" data-testid="widget-menu-delete" onClick={onRemove}>
          Delete widget
        </button>
      )}
    </div>
  );
}

/**
 * The `⠿` *Move widget* grip (**P-180**). It used to be a `<button>` with nothing on it: no
 * pointer handler, no `draggable`, no droppable under the grid — beside a resize handle that
 * worked, which is what made a dashboard's layout read as broken rather than unbuilt.
 *
 * It now answers all three gestures a handle has to answer, and they cannot double-commit:
 *
 *  - **`draggable`** — the browser's own drag controller. `dragstart` records the grab and marks
 *    it `native`, the grid's `dragover`/`drop` finish it. This is also the attribute a verifier
 *    reads to know the element claims to be draggable at all.
 *  - **pointer** — the same pointer-capture path `ResizeHandle` uses, for the case where the
 *    native controller never starts (a touch drag, a headless driver that only moves a pointer).
 *    A native drag fires `pointercancel`, and `native` makes the pointer commit stand down.
 *  - **keyboard** — `Space` picks the card up, the arrows move it a whole grid unit at a time
 *    and `Space` drops it, which is the accessibility half of the same parity claim.
 *
 * Every path ends in one `mutations.move([{id, x, y, w, h}])`, and a drop where the drag began
 * writes nothing (D1 acceptance W2).
 */
function WidgetGrip({
  widget,
  source,
  drag,
  onCommitDrag,
}: {
  widget: WidgetRow;
  source: DashboardSource;
  drag: React.MutableRefObject<WidgetDrag | null>;
  onCommitDrag: (grid: Element | null, clientX: number, clientY: number) => void;
}) {
  /** The keyboard drag in progress: the layout being moved, and by how many units so far. */
  const [keyboardDrag, setKeyboardDrag] = useState<{ dx: number; dy: number } | null>(null);
  const writable = source.mutations !== null;

  const begin = (clientX: number, clientY: number, native: boolean) => {
    drag.current = { id: widget.id, layout: widget.layout, startX: clientX, startY: clientY, native };
  };

  return (
    <button
      type="button"
      className="ez-widget-card__grip"
      data-testid="widget-grip"
      aria-label="Move widget"
      aria-grabbed={keyboardDrag === null ? undefined : true}
      draggable={writable}
      onDragStart={(event) => {
        if (!writable) return;
        begin(event.clientX, event.clientY, true);
        event.dataTransfer.effectAllowed = 'move';
        // Firefox starts no drag at all without a payload.
        event.dataTransfer.setData('text/plain', widget.id);
      }}
      onDragEnd={() => {
        drag.current = null;
      }}
      onPointerDown={(event) => {
        if (!writable) return;
        begin(event.clientX, event.clientY, false);
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerCancel={() => {
        // The native drag controller took over; `drop` will finish the gesture.
        if (drag.current !== null) drag.current.native = true;
      }}
      onPointerUp={(event) => {
        const origin = drag.current;
        if (origin === null || origin.native) return;
        onCommitDrag(event.currentTarget.closest('.ez-widget-grid'), event.clientX, event.clientY);
      }}
      onKeyDown={(event) => {
        if (!writable) return;
        if (event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          if (keyboardDrag === null) {
            setKeyboardDrag({ dx: 0, dy: 0 });
            return;
          }
          const next = moveBy(widget.layout, keyboardDrag.dx, keyboardDrag.dy);
          setKeyboardDrag(null);
          if (!sameLayout(next, widget.layout)) source.mutations?.move([{ id: widget.id, ...next }]);
          return;
        }
        if (event.key === 'Escape' && keyboardDrag !== null) {
          event.preventDefault();
          setKeyboardDrag(null);
          return;
        }
        if (keyboardDrag === null) return;
        const step =
          event.key === 'ArrowRight'
            ? { dx: 1, dy: 0 }
            : event.key === 'ArrowLeft'
              ? { dx: -1, dy: 0 }
              : event.key === 'ArrowDown'
                ? { dx: 0, dy: 1 }
                : event.key === 'ArrowUp'
                  ? { dx: 0, dy: -1 }
                  : null;
        if (step === null) return;
        event.preventDefault();
        setKeyboardDrag({ dx: keyboardDrag.dx + step.dx, dy: keyboardDrag.dy + step.dy });
      }}
    >
      ⠿
    </button>
  );
}

/**
 * The widget's name, renamed in place (**P-181**). `dashboards.updateWidget` takes a `name` and
 * `useDashboardSource` has exposed `mutations.rename` since M7 with no call site, so every widget
 * on every dashboard was stuck with the title its kind was created under.
 *
 * It is an input inside the measured title row rather than beside it, so the header's geometry —
 * 32 px tall, 25 px in from the card's left edge — is the one `specs/M10-D1-dashboards.md` §2.2
 * measures. It commits on blur and on `Enter`, and `Escape` puts the stored name back.
 */
function WidgetTitleInput({ widget, source }: { widget: WidgetRow; source: DashboardSource }) {
  const [draft, setDraft] = useState(widget.name);
  const committed = useRef(widget.name);
  if (committed.current !== widget.name) {
    committed.current = widget.name;
    if (draft !== widget.name) setDraft(widget.name);
  }

  const commit = () => {
    const next = draft.trim();
    if (next === '' || next === widget.name) {
      setDraft(widget.name);
      return;
    }
    source.mutations?.rename(widget.id, next);
  };

  return (
    <input
      type="text"
      className="ez-widget-card__title-input"
      data-testid="widget-title-input"
      aria-label={`Rename ${widget.name}`}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(widget.name);
        }
      }}
    />
  );
}

/**
 * Resize by dragging the corner. The pointer's travel is converted to whole grid units — the
 * same 12-column / 56 px-row / 16 px-margin unit the cards are laid out in — and a drag that
 * ends where it started writes nothing (D1 acceptance W2, round 4's no-op rule).
 */
function ResizeHandle({ widget, source }: { widget: WidgetRow; source: DashboardSource }) {
  const start = useRef<{ x: number; y: number; layout: WidgetLayout; unitX: number } | null>(null);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (source.mutations === null) return;
      const grid = event.currentTarget.closest('.ez-widget-grid');
      const gridWidth = grid?.getBoundingClientRect().width ?? 1073;
      const unitX = (gridWidth - WIDGET_GRID.margin * (WIDGET_GRID.columns - 1)) / WIDGET_GRID.columns;
      start.current = { x: event.clientX, y: event.clientY, layout: widget.layout, unitX };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [source.mutations, widget.layout],
  );

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const origin = start.current;
      start.current = null;
      if (origin === null || source.mutations === null) return;
      const unitY = WIDGET_GRID.rowHeight + WIDGET_GRID.margin;
      const dw = Math.round((event.clientX - origin.x) / (origin.unitX + WIDGET_GRID.margin));
      const dh = Math.round((event.clientY - origin.y) / unitY);
      const next = {
        ...origin.layout,
        w: Math.max(1, Math.min(WIDGET_GRID.columns - origin.layout.x, origin.layout.w + dw)),
        h: Math.max(1, origin.layout.h + dh),
      };
      if (next.w === origin.layout.w && next.h === origin.layout.h) return;
      source.mutations.move([{ id: widget.id, ...next }]);
    },
    [source.mutations, widget.id],
  );

  if (source.mutations === null) return null;
  return (
    <button
      type="button"
      className="ez-widget-card__resize"
      data-testid="widget-resize"
      aria-label={`Resize ${widget.name}`}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    />
  );
}
